import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { ToastContainer, ToastType } from '../components/Toast';
import AttributeLabel from '../components/AttributeLabel';
import DocumentViewer from '../components/DocumentViewer';
import ConfirmationModal from '../components/ConfirmationModal';
import KassenpruefungIssues from '../components/KassenpruefungIssues';
import { ModalShell, ModalActions } from '../components/modals/core';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  LockClosedIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalculatorIcon,
  TagIcon,
  ChevronDownIcon,
  UserGroupIcon,
  ChartBarSquareIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

type ToastItem = { id: string; type: ToastType; message: string };

interface Assignment { id: number; year: number; status: string; completed_at: string | null; total_bookings: number; checked_count: number; }
interface Account { id: number; name: string; startbetrag: number; anzahl: number; summe: number; kontoauszug_anzahl: number; }
interface Totals { total_transactions: number; einnahmen: number; ausgaben: number; }
interface Progress { confirmed: number; flagged: number; checked: number; total: number; }
interface Auditor { id: number; member_id: number; status: string; completed_at: string | null; vorname: string | null; name: string | null; }
interface TransactionCategory { id: number | null; name: string; betrag: number | string; }
interface Transaction {
  id: number; buchungstag: string; name_zahlungsbeteiligter: string | null; buchungstext: string | null;
  verwendungszweck: string | null; betrag: number | string; saldo: number | string | null; konto_id: number;
  konto_name: string | null; check_status: 'confirmed' | 'flagged' | null; check_note: string | null; doc_count: number;
  kategorie: string | null;
  kategorien?: TransactionCategory[];
  own_check_status?: 'confirmed' | 'flagged' | null;
  own_check_note?: string | null;
  approved_by_member_id?: number | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
}
interface Beleg {
  id: number;
  name: string;
  file_path?: string;
  size: number | null;
  created_at: string;
  buchung_id: number;
  sourceUrl?: string;
  allowDownload?: boolean;
}
interface BankStatement {
  id: number;
  document_id: number;
  zeitraum_von: string;
  zeitraum_bis: string;
  auszugsnummer: string | null;
  bank_name: string | null;
  iban_ende: string | null;
  anfangssaldo: number | string | null;
  endsaldo: number | string | null;
  waehrung: string;
  anzahl_buchungen: number | null;
  document_name: string;
  document_size: number | null;
  document_created_at: string;
}
interface ChecklistItem { id: number; text: string; description?: string; checked: boolean; }
interface CategoryConfirmTarget { category: string; transactionIds: number[]; }
interface MonthGroup { key: string; label: string; items: Transaction[]; sum: number; }
interface BudgetComparisonRow {
  category: string;
  planned_income: number;
  actual_income: number;
  income_variance: number;
  planned_expense: number;
  actual_expense: number;
  expense_variance: number;
  expense_usage_percent: number | null;
  status: 'ok' | 'warning' | 'exceeded' | 'unplanned' | 'below-plan';
}
interface BudgetComparisonTotals {
  planned_income: number;
  planned_expense: number;
  planned_balance: number;
  actual_income: number;
  actual_expense: number;
  actual_balance: number;
  income_variance: number;
  expense_variance: number;
  balance_variance: number;
}
interface BudgetComparisonPlan {
  id: number;
  name: string;
  year: number;
  importYear: number;
  totals: BudgetComparisonTotals;
  rows: BudgetComparisonRow[];
}
interface BudgetComparisonResponse {
  success: boolean;
  year: number;
  hasPlan: boolean;
  plans: BudgetComparisonPlan[];
}

const fmtMoney = (n: number | string | null) =>
  (Number(n || 0)).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('de-DE') : '—');
const fmtFileSize = (bytes: number | null) => {
  if (!bytes || bytes < 1) return 'Größe unbekannt';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
};
const approvalText = (tx: Transaction, currentMemberId: number | null): string | null => {
  if (!tx.approved_at) return null;
  const date = new Date(tx.approved_at);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const isOwnApproval = currentMemberId !== null
    && Number(tx.approved_by_member_id) === currentMemberId;
  const subject = isOwnApproval ? 'Du' : (tx.approved_by_name || 'Ein Kassenprüfer');
  return `${subject} ${isOwnApproval ? 'hast' : 'hat'} am ${day} um ${time} Uhr genehmigt.`;
};

const cardClass = 'rounded-2xl bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-gray-700 shadow-sm';

const UNCATEGORIZED = 'Nicht kategorisiert';
/** Echte Buchungskategorie (primärer Split); Fallback für nicht kategorisierte Buchungen. */
const txCategory = (tx: { kategorie: string | null }): string => tx.kategorie || UNCATEGORIZED;
const txCategories = (tx: Transaction): TransactionCategory[] => tx.kategorien?.length
  ? tx.kategorien
  : [{ id: null, name: txCategory(tx), betrag: tx.betrag }];

const csvCell = (value: string | number | null | undefined): string => {
  const normalized = String(value ?? '').replace(/\r?\n/g, ' ').trim();
  // Verhindert, dass Tabellenprogramme Inhalte aus Freitextfeldern als Formel ausführen.
  const safeValue = /^[=+\-@]/.test(normalized) ? `'${normalized}` : normalized;
  return `"${safeValue.replace(/"/g, '""')}"`;
};

const csvMoney = (value: number | string | null): string =>
  Number(value || 0).toFixed(2).replace('.', ',');

const Kassenpruefer: React.FC = () => {
  const { realUser, user: authUser } = useContext(AuthContext);
  const authenticatedMemberId = Number(realUser?.mg_nr ?? authUser?.mg_nr);
  const currentMemberId = Number.isFinite(authenticatedMemberId) ? authenticatedMemberId : null;
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const [totals, setTotals] = useState<Totals | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [auditors, setAuditors] = useState<Auditor[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [belege, setBelege] = useState<Beleg[]>([]);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [report, setReport] = useState('');
  const [budgetComparison, setBudgetComparison] = useState<BudgetComparisonResponse | null>(null);
  const [selectedBudgetPlanId, setSelectedBudgetPlanId] = useState<number | null>(null);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [budgetPdfLoading, setBudgetPdfLoading] = useState(false);

  const [noteModal, setNoteModal] = useState<{ tx: Transaction } | null>(null);
  const [noteText, setNoteText] = useState('');
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<Set<number>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkFlagOpen, setBulkFlagOpen] = useState(false);
  const [bulkFlagging, setBulkFlagging] = useState(false);
  const [bulkNoteText, setBulkNoteText] = useState('');
  const [categoryConfirmTarget, setCategoryConfirmTarget] = useState<CategoryConfirmTarget | null>(null);
  const [previewDocument, setPreviewDocument] = useState<Beleg | null>(null);
  const [statementAccount, setStatementAccount] = useState<Account | null>(null);
  const [bankStatements, setBankStatements] = useState<BankStatement[]>([]);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [statementsError, setStatementsError] = useState<string | null>(null);
  const [downloadStatement, setDownloadStatement] = useState<BankStatement | null>(null);
  const [statementDownloading, setStatementDownloading] = useState(false);

  // Filter
  const [kontoFilter, setKontoFilter] = useState<number | null>(null);
  const [docFilter, setDocFilter] = useState<'all' | 'with' | 'without'>('all');
  const [amountFilter, setAmountFilter] = useState<'all' | 'in' | 'out'>('all');
  const [hideChecked, setHideChecked] = useState(false);
  const [issueBookingIds, setIssueBookingIds] = useState<number[] | null>(null);
  const applyIssueFilter = useCallback((ids: number[] | null) => setIssueBookingIds(ids), []);
  const [sortMode, setSortMode] = useState<'date' | 'category'>('date');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [mobileVisibleCount, setMobileVisibleCount] = useState(20);

  const currentAssignment = assignments.find(a => a.year === year) || null;
  const isCompleted = currentAssignment?.status === 'completed';
  const selectedBudgetPlan = budgetComparison?.plans.find(plan => plan.id === selectedBudgetPlanId)
    || budgetComparison?.plans[0]
    || null;
  const balance = Number(totals?.einnahmen || 0) - Number(totals?.ausgaben || 0);
  const progressPercent = progress?.total
    ? Math.min(100, Math.round((Number(progress.checked || 0) / Number(progress.total)) * 100))
    : 0;

  const addToast = (type: ToastType, message: string) => {
    const id = Date.now().toString() + Math.random();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };
  const removeToast = (id: string) => setToasts(prev => prev.filter(t => t.id !== id));

  // Zugewiesene Jahre laden
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/api/kassenpruefung/my-assignments');
        if (res.data.success) {
          const list: Assignment[] = res.data.assignments || [];
          setAssignments(list);
          if (list.length > 0) setYear(list[0].year);
        }
      } catch (e) {
        addToast('error', 'Zuweisungen konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadYear = useCallback(async (y: number) => {
    try {
      const [sum, tx, docs, chk, budget] = await Promise.all([
        apiClient.get(`/api/kassenpruefung/${y}/summary`),
        apiClient.get(`/api/kassenpruefung/${y}/transactions`),
        apiClient.get(`/api/kassenpruefung/${y}/documents`),
        apiClient.get(`/api/kassenpruefung/${y}/checklist`),
        apiClient.get(`/api/kassenpruefung/${y}/budget-comparison`),
      ]);
      if (sum.data.success) {
        setTotals(sum.data.totals);
        setAccounts(sum.data.accounts || []);
        setProgress(sum.data.progress);
        setAuditors(sum.data.auditors || []);
      }
      if (tx.data.success) setTransactions(tx.data.transactions || []);
      if (docs.data.success) setBelege(docs.data.documents || []);
      if (chk.data.success) { setChecklist(chk.data.checklist || []); setReport(chk.data.report || ''); }
      if (budget.data.success) {
        const comparison: BudgetComparisonResponse = budget.data;
        setBudgetComparison(comparison);
        setSelectedBudgetPlanId(previous => (
          comparison.plans.some(plan => plan.id === previous) ? previous : (comparison.plans[0]?.id || null)
        ));
      }
    } catch (e) {
      addToast('error', 'Daten für dieses Jahr konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => {
    if (year) {
      setBudgetComparison(null);
      setBudgetModalOpen(false);
      loadYear(year);
      setKontoFilter(null);
      setDocFilter('all');
      setAmountFilter('all');
      setHideChecked(false);
      setIssueBookingIds(null);
      setStatementAccount(null);
      setBankStatements([]);
      setDownloadStatement(null);
    }
  }, [year, loadYear]);

  const filteredTx = transactions.filter(t => {
    if (issueBookingIds !== null && !issueBookingIds.includes(t.id)) return false;
    if (kontoFilter !== null && t.konto_id !== kontoFilter) return false;
    if (docFilter === 'with' && !t.doc_count) return false;
    if (docFilter === 'without' && t.doc_count) return false;
    if (amountFilter === 'in' && Number(t.betrag) <= 0) return false;
    if (amountFilter === 'out' && Number(t.betrag) >= 0) return false;
    if (hideChecked && t.check_status !== null) return false;
    return true;
  });
  const filtersActive = kontoFilter !== null || docFilter !== 'all' || amountFilter !== 'all' || hideChecked;
  const resetFilters = () => { setKontoFilter(null); setDocFilter('all'); setAmountFilter('all'); setHideChecked(false); };

  useEffect(() => {
    setMobileVisibleCount(20);
    setSelectedTransactionIds(new Set());
    setBulkConfirmOpen(false);
    setBulkFlagOpen(false);
    setBulkNoteText('');
    setCategoryConfirmTarget(null);
    setCollapsedCategories(new Set());
  }, [kontoFilter, docFilter, amountFilter, hideChecked, year, issueBookingIds]);

  const allFilteredSelected = filteredTx.length > 0
    && filteredTx.every(tx => selectedTransactionIds.has(tx.id));
  const selectedBookingLabel = selectedTransactionIds.size === 1
    ? '1 Buchung'
    : `${selectedTransactionIds.size} Buchungen`;
  const selectedUnconfirmedCount = transactions.filter(
    tx => selectedTransactionIds.has(tx.id) && tx.check_status !== 'confirmed'
  ).length;
  const selectedConfirmLabel = selectedUnconfirmedCount === 1
    ? '1 Buchung'
    : `${selectedUnconfirmedCount} Buchungen`;

  const toggleTransactionSelection = (id: number) => {
    setSelectedTransactionIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedTransactionIds(previous => {
      const next = new Set(previous);
      if (allFilteredSelected) {
        filteredTx.forEach(tx => next.delete(tx.id));
      } else {
        filteredTx.forEach(tx => next.add(tx.id));
      }
      return next;
    });
  };

  const toggleCategory = (category: string) => {
    setCollapsedCategories(previous => {
      const next = new Set(previous);
      if (next.has(category)) next.delete(category); else next.add(category);
      return next;
    });
  };

  // Prüfstatus setzen
  const setCheck = async (tx: Transaction, status: 'confirmed' | 'flagged' | 'none', note?: string) => {
    if (!year) return;
    try {
      const res = await apiClient.post(`/api/kassenpruefung/${year}/check/${tx.id}`, { status, note });
      if (res.data.success) {
        await loadYear(year);
      }
    } catch (e) {
      addToast('error', 'Prüfvermerk konnte nicht gespeichert werden');
    }
  };

  const openNoteModal = (tx: Transaction) => { setNoteText(tx.check_note || ''); setNoteModal({ tx }); };
  const saveNote = async () => {
    if (!noteModal) return;
    await setCheck(noteModal.tx, 'flagged', noteText);
    setNoteModal(null);
    addToast('warning', 'Buchung beanstandet');
  };

  const confirmSelectedTransactions = async () => {
    if (!year || selectedTransactionIds.size === 0) return;
    const selectedIds = transactions
      .filter(tx => selectedTransactionIds.has(tx.id) && tx.check_status !== 'confirmed')
      .map(tx => tx.id);
    if (selectedIds.length === 0) {
      setSelectedTransactionIds(new Set());
      setBulkConfirmOpen(false);
      addToast('success', 'Die Auswahl ist bereits genehmigt');
      return;
    }
    setBulkConfirming(true);
    try {
      const res = await apiClient.post(`/api/kassenpruefung/${year}/check-bulk`, { buchung_ids: selectedIds });
      if (res.data.success) {
        await loadYear(year);
        setSelectedTransactionIds(new Set());
        setBulkConfirmOpen(false);
        addToast('success', selectedIds.length === 1 ? '1 Buchung bestätigt' : `${selectedIds.length} Buchungen bestätigt`);
      }
    } catch (e) {
      addToast('error', 'Ausgewählte Buchungen konnten nicht bestätigt werden');
    } finally {
      setBulkConfirming(false);
    }
  };

  const confirmCategoryTransactions = async () => {
    if (!year || !categoryConfirmTarget || categoryConfirmTarget.transactionIds.length === 0) return;
    const { category, transactionIds } = categoryConfirmTarget;
    setBulkConfirming(true);
    try {
      const res = await apiClient.post(`/api/kassenpruefung/${year}/check-bulk`, { buchung_ids: transactionIds });
      if (res.data.success) {
        await loadYear(year);
        setCategoryConfirmTarget(null);
        addToast('success', `${transactionIds.length} Buchungen in „${category}“ genehmigt`);
      }
    } catch (e) {
      addToast('error', `Kategorie „${category}“ konnte nicht genehmigt werden`);
    } finally {
      setBulkConfirming(false);
    }
  };

  const openBulkFlagModal = () => {
    setBulkNoteText('');
    setBulkFlagOpen(true);
  };

  const flagSelectedTransactions = async () => {
    const note = bulkNoteText.trim();
    if (!year || selectedTransactionIds.size === 0 || !note) return;
    const selectedIds = Array.from(selectedTransactionIds);
    setBulkFlagging(true);
    try {
      const res = await apiClient.post(`/api/kassenpruefung/${year}/check-bulk`, {
        buchung_ids: selectedIds,
        status: 'flagged',
        note,
      });
      if (res.data.success) {
        await loadYear(year);
        setSelectedTransactionIds(new Set());
        setBulkFlagOpen(false);
        setBulkNoteText('');
        addToast('warning', selectedIds.length === 1 ? '1 Buchung beanstandet' : `${selectedIds.length} Buchungen beanstandet`);
      }
    } catch (e) {
      addToast('error', 'Ausgewählte Buchungen konnten nicht beanstandet werden');
    } finally {
      setBulkFlagging(false);
    }
  };

  const openDocument = (doc: Beleg) => {
    if (!year) return;
    setPreviewDocument({
      ...doc,
      sourceUrl: `/api/kassenpruefung/${year}/documents/${doc.id}/preview`,
      allowDownload: true,
    });
  };

  const openBankStatements = async (account: Account) => {
    if (!year || Number(account.kontoauszug_anzahl || 0) === 0) return;
    setStatementAccount(account);
    setBankStatements([]);
    setStatementsError(null);
    setStatementsLoading(true);
    try {
      const response = await apiClient.get(`/api/kassenpruefung/${year}/accounts/${account.id}/bank-statements`);
      setBankStatements(response.data.statements || []);
    } catch (error: any) {
      setStatementsError(error.response?.data?.message || 'Kontoauszüge konnten nicht geladen werden');
    } finally {
      setStatementsLoading(false);
    }
  };

  const previewBankStatement = (statement: BankStatement) => {
    if (!year) return;
    setPreviewDocument({
      id: statement.document_id,
      name: statement.document_name,
      file_path: statement.document_name,
      size: statement.document_size,
      created_at: statement.document_created_at,
      buchung_id: 0,
      sourceUrl: `/api/kassenpruefung/${year}/bank-statements/${statement.id}/preview`,
      allowDownload: false,
    });
  };

  const confirmBankStatementDownload = async () => {
    if (!year || !downloadStatement) return;
    const statement = downloadStatement;
    setStatementDownloading(true);
    try {
      const response = await apiClient.get(
        `/api/kassenpruefung/${year}/bank-statements/${statement.id}/download`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${statementAccount?.name || 'Kontoauszug'}_${statement.zeitraum_von}_${statement.zeitraum_bis}_VERTRAULICH.pdf`
        .replace(/[^a-zA-Z0-9äöüÄÖÜß._-]+/g, '_');
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setDownloadStatement(null);
      addToast('success', 'Markierte PDF heruntergeladen. Der Zugriff wurde protokolliert.');
    } catch (error: any) {
      addToast('error', error.response?.data?.message || 'Kontoauszug konnte nicht heruntergeladen werden');
    } finally {
      setStatementDownloading(false);
    }
  };

  const toggleChecklist = (id: number) => {
    setChecklist(prev => prev.map(i => i.id === id ? { ...i, checked: !i.checked } : i));
  };
  const saveChecklist = async () => {
    if (!year) return;
    try {
      await apiClient.post(`/api/kassenpruefung/${year}/checklist`, { checklist });
      await apiClient.post(`/api/kassenpruefung/${year}/report`, { report });
      addToast('success', 'Checkliste & Kommentar gespeichert');
    } catch (e) {
      addToast('error', 'Speichern fehlgeschlagen');
    }
  };

  const completeAudit = async () => {
    if (!year) return;
    if (!window.confirm(`Prüfung für ${year} wirklich abschließen? Danach sind keine Änderungen mehr möglich.`)) return;
    try {
      await apiClient.post(`/api/kassenpruefung/${year}/checklist`, { checklist });
      await apiClient.post(`/api/kassenpruefung/${year}/report`, { report });
      await apiClient.post(`/api/kassenpruefung/${year}/complete`, {});
      setAssignments(prev => prev.map(a => a.year === year ? { ...a, status: 'completed' } : a));
      addToast('success', 'Prüfung abgeschlossen');
    } catch (e) {
      addToast('error', 'Abschluss fehlgeschlagen');
    }
  };

  const downloadProtocol = async () => {
    if (!year) return;
    try {
      const res = await apiClient.get(`/api/kassenpruefung/${year}/protocol-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Kassenpruefprotokoll_${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      addToast('error', 'Protokoll konnte nicht erstellt werden');
    }
  };

  const downloadBudgetComparisonPdf = async () => {
    if (!year || !selectedBudgetPlan) return;
    setBudgetPdfLoading(true);
    try {
      const res = await apiClient.get(`/api/kassenpruefung/${year}/budget-comparison-pdf`, {
        params: { planId: selectedBudgetPlan.id },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      const safeName = selectedBudgetPlan.name.replace(/[^a-zA-Z0-9äöüÄÖÜß_-]+/g, '_');
      link.href = url;
      link.download = `Haushaltsplanvergleich_${year}_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      addToast('success', 'Haushaltsplanvergleich als PDF erstellt');
    } catch (e) {
      addToast('error', 'Haushaltsplanvergleich konnte nicht erstellt werden');
    } finally {
      setBudgetPdfLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!year || filteredTx.length === 0) return;

    const documentsByTransaction = belege.reduce<Record<number, string[]>>((acc, document) => {
      (acc[document.buchung_id] = acc[document.buchung_id] || []).push(document.name);
      return acc;
    }, {});
    const headers = [
      'Buchungs-ID',
      'Datum',
      'Konto',
      'Zahlungsbeteiligter',
      'Buchungstext',
      'Verwendungszweck',
      'Kategorie',
      'Betrag (EUR)',
      'Saldo (EUR)',
      'Beleganzahl',
      'Belege',
      'Prüfstatus',
      'Prüfnotiz',
    ];
    const rows = filteredTx.map(tx => {
      const documentNames = documentsByTransaction[tx.id] || [];
      const checkStatus = tx.check_status === 'confirmed'
        ? 'Bestätigt'
        : tx.check_status === 'flagged'
          ? 'Beanstandet'
          : 'Offen';

      const categorySummary = txCategories(tx)
        .map(category => `${category.name} (${fmtMoney(category.betrag)})`)
        .join(' | ');

      return [
        tx.id,
        fmtDate(tx.buchungstag),
        tx.konto_name,
        tx.name_zahlungsbeteiligter,
        tx.buchungstext,
        tx.verwendungszweck,
        categorySummary,
        csvMoney(tx.betrag),
        tx.saldo === null ? '' : csvMoney(tx.saldo),
        documentNames.length,
        documentNames.join(' | '),
        checkStatus,
        tx.check_note,
      ].map(csvCell).join(';');
    });
    const csv = `\uFEFF${[headers.map(csvCell).join(';'), ...rows].join('\r\n')}\r\n`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `Kassenpruefung_${year}${filtersActive ? '_gefiltert' : ''}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    addToast('success', `${rows.length} Buchungen als CSV exportiert`);
  };

  const renderPageHeader = (actions?: React.ReactNode) => (
    <>
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <ShieldCheckIcon className="h-7 w-7 text-primary-600 dark:text-primary-400 flex-shrink-0" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Kassenprüfung</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Buchungen, Belege und Prüfvermerke kontrollieren</p>
            </div>
          </div>
          {actions && (
            <div className="flex flex-wrap justify-end items-center gap-2 md:gap-3 flex-shrink-0">
              {actions}
            </div>
          )}
        </div>
      </header>

      <header className="app-mobile-page-header sm:hidden bg-white dark:bg-gray-800 px-4 py-3 border-b-[3px] border-lime-400 mb-4 flex items-center gap-3">
        <ShieldCheckIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">Kassenprüfung</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Belege und Buchungen prüfen</p>
        </div>
      </header>
    </>
  );

  if (loading) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Lädt…</div>;
  }

  if (assignments.length === 0) {
    return (
      <div className="font-sans text-gray-800 dark:text-slate-200">
        <div className="max-w-7xl mx-auto">
          {renderPageHeader()}
          <div className="pb-8 sm:pb-10">
            <div className={`${cardClass} p-8 text-center`}>
              <ShieldCheckIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Keine Prüfungen zugewiesen</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                Sie wurden aktuell für kein Jahr als Kassenprüfer benannt.
              </p>
            </div>
          </div>
        </div>
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    );
  }

  const belegeByBuchung = belege.reduce<Record<number, Beleg[]>>((acc, b) => {
    (acc[b.buchung_id] = acc[b.buchung_id] || []).push(b); return acc;
  }, {});
  // Gruppierung nach echter Buchungskategorie (größte Kategorien zuerst, „Nicht kategorisiert" ans Ende)
  const txGroups = sortMode === 'category'
    ? (() => {
        const map = new Map<string, Transaction[]>();
        filteredTx.forEach(tx => {
          const c = txCategory(tx);
          const arr = map.get(c);
          if (arr) arr.push(tx); else map.set(c, [tx]);
        });
        return Array.from(map.entries())
          .map(([category, items]) => ({ category, items, sum: items.reduce((s, t) => s + Number(t.betrag), 0) }))
          .sort((a, b) => {
            if (a.category === UNCATEGORIZED) return 1;
            if (b.category === UNCATEGORIZED) return -1;
            return Math.abs(b.sum) - Math.abs(a.sum);
          });
      })()
    : [];

  const listMonthGroups: MonthGroup[] = sortMode === 'date'
    ? (() => {
        const groups = new Map<string, MonthGroup>();
        filteredTx.forEach(tx => {
          const date = new Date(tx.buchungstag);
          const validDate = !Number.isNaN(date.getTime());
          const key = validDate
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            : 'unknown';
          const existing = groups.get(key);
          if (existing) {
            existing.items.push(tx);
            existing.sum += Number(tx.betrag);
          } else {
            groups.set(key, {
              key,
              label: validDate
                ? date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
                : 'Ohne Datum',
              items: [tx],
              sum: Number(tx.betrag),
            });
          }
        });
        return Array.from(groups.values());
      })()
    : [];

  let remainingMobileTransactions = mobileVisibleCount;
  const visibleMobileMonthGroups = listMonthGroups
    .map(group => {
      const visibleItems = group.items.slice(0, Math.max(0, remainingMobileTransactions));
      remainingMobileTransactions -= visibleItems.length;
      return { ...group, visibleItems };
    })
    .filter(group => group.visibleItems.length > 0);

  const monthDividerMobile = (group: MonthGroup) => (
    <div
      key={`month-mobile-${group.key}`}
      className="flex items-center gap-2 border-y border-gray-100 bg-gray-50/70 px-3 py-2 dark:border-gray-700 dark:bg-[#151c28]"
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500/70" aria-hidden="true" />
      <span className="text-xs font-semibold capitalize text-gray-700 dark:text-gray-200">{group.label}</span>
      <span className="text-[10px] tabular-nums text-gray-400 dark:text-gray-500">
        {group.items.length} {group.items.length === 1 ? 'Buchung' : 'Buchungen'}
      </span>
      <span className="h-px min-w-3 flex-1 bg-gray-200/80 dark:bg-gray-700" aria-hidden="true" />
      <span className={`text-[11px] font-semibold tabular-nums ${group.sum >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {fmtMoney(group.sum)}
      </span>
    </div>
  );

  const monthDividerDesktop = (group: MonthGroup) => (
    <tr className="border-y border-gray-100 bg-gray-50/70 dark:border-gray-700 dark:bg-[#151c28]">
      <td colSpan={7} className="px-4 py-2">
        <div className="flex items-center gap-3">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500/70" aria-hidden="true" />
          <span className="text-xs font-semibold capitalize tracking-wide text-gray-700 dark:text-gray-200">{group.label}</span>
          <span className="text-[10px] tabular-nums text-gray-400 dark:text-gray-500">
            {group.items.length} {group.items.length === 1 ? 'Buchung' : 'Buchungen'}
          </span>
          <span className="h-px min-w-6 flex-1 bg-gray-200/80 dark:bg-gray-700" aria-hidden="true" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Bilanz</span>
          <span className={`text-xs font-semibold tabular-nums ${group.sum >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {fmtMoney(group.sum)}
          </span>
        </div>
      </td>
    </tr>
  );

  const renderMobileCard = (tx: Transaction) => {
    const docs = belegeByBuchung[tx.id] || [];
    const isIncome = Number(tx.betrag) >= 0;
    const isSelected = selectedTransactionIds.has(tx.id);
    const isFlagged = tx.check_status === 'flagged';
    const approval = approvalText(tx, currentMemberId);
    const approvedByOther = tx.check_status === 'confirmed' && tx.own_check_status !== 'confirmed';
    return (
      <article
        key={tx.id}
        className={`p-3.5 transition-[background-color,box-shadow] duration-150 ${
          isFlagged
            ? `${isSelected ? 'bg-amber-50/50 dark:bg-amber-900/20' : ''} hover:bg-amber-50/80 hover:shadow-[inset_3px_0_0_0_#f59e0b] dark:hover:bg-amber-900/30`
            : isSelected
              ? 'bg-primary-50/70 hover:bg-primary-100/70 hover:shadow-[inset_3px_0_0_0_#22c55e] dark:bg-primary-900/10 dark:hover:bg-primary-900/20'
              : 'hover:bg-primary-50/40 hover:shadow-[inset_3px_0_0_0_#22c55e] dark:hover:bg-primary-900/10'
        }`}
      >
        <div className="flex items-start gap-3">
          {!isCompleted && !approvedByOther && (
            <input
              type="checkbox"
              checked={selectedTransactionIds.has(tx.id)}
              onChange={() => toggleTransactionSelection(tx.id)}
              aria-label={`Buchung vom ${fmtDate(tx.buchungstag)} auswählen`}
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          )}
          <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
            <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
              <span>{fmtDate(tx.buchungstag)}</span>
              <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">•</span>
              <span className="truncate">{tx.konto_name || 'Kein Konto'}</span>
            </div>
            <h3 className="mt-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {tx.name_zahlungsbeteiligter || tx.buchungstext || 'Ohne Bezeichnung'}
            </h3>
            </div>
            <div className={`shrink-0 whitespace-nowrap text-sm font-bold tabular-nums ${isIncome ? 'text-green-600' : 'text-red-600'}`}>
              {fmtMoney(tx.betrag)}
            </div>
          </div>
        </div>

        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {txCategories(tx).map((category, index) => (
            <span
              key={`${category.id ?? 'uncategorized'}-${index}`}
              title={`Kategorieanteil: ${fmtMoney(category.betrag)}`}
              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              <TagIcon className="h-3 w-3" />
              <span>{category.name}</span>
              <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">·</span>
              <span className="tabular-nums">{fmtMoney(category.betrag)}</span>
            </span>
          ))}
        </div>

        {tx.verwendungszweck && (
          <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
            {tx.verwendungszweck}
          </p>
        )}

        {tx.check_status === 'flagged' && tx.check_note && (
          <div className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs leading-5 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            {tx.check_note}
          </div>
        )}

        {docs.length > 0 && (
          <div className="mt-3 grid gap-2">
            {docs.map((doc, index) => (
              <button
                key={doc.id}
                onClick={() => openDocument(doc)}
                type="button"
                className="inline-flex min-h-11 touch-manipulation select-none items-center justify-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-3 text-sm font-semibold text-primary-700 transition-colors active:bg-primary-100 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300 dark:active:bg-primary-900/40"
              >
                <DocumentTextIcon className="h-5 w-5" />
                {docs.length > 1 ? `Beleg ${index + 1}` : 'Beleg öffnen'}
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 border-t border-gray-100 pt-3 dark:border-gray-800">
          <span className="mb-2 block text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Prüfung
          </span>
          <div className="grid grid-cols-2 gap-3">
            <button
              disabled={isCompleted || approvedByOther}
              onClick={() => setCheck(tx, tx.own_check_status === 'confirmed' ? 'none' : 'confirmed')}
              type="button"
              aria-pressed={tx.check_status === 'confirmed'}
              title={approvedByOther ? `Bereits von ${tx.approved_by_name || 'einem Mitprüfer'} genehmigt` : undefined}
              className={`inline-flex min-h-11 touch-manipulation select-none items-center justify-center gap-1.5 rounded-xl border px-2 text-xs font-semibold transition-colors active:scale-[0.98] disabled:opacity-40 ${
                tx.check_status === 'confirmed'
                  ? 'border-green-600 bg-green-600 text-white'
                  : 'border-green-200 text-green-700 active:bg-green-50 dark:border-green-800 dark:text-green-400 dark:active:bg-green-900/20'
              }`}
            >
              <CheckCircleIcon className="h-5 w-5" />
              {tx.check_status === 'confirmed' ? 'Bestätigt' : 'Bestätigen'}
            </button>
            <button
              disabled={isCompleted || approvedByOther}
              onClick={() => openNoteModal(tx)}
              type="button"
              aria-pressed={tx.check_status === 'flagged'}
              className={`inline-flex min-h-11 touch-manipulation select-none items-center justify-center gap-1.5 rounded-xl border px-2 text-xs font-semibold transition-colors active:scale-[0.98] disabled:opacity-40 ${
                tx.check_status === 'flagged'
                  ? 'border-amber-500 bg-amber-500 text-white'
                  : 'border-amber-200 text-amber-700 active:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:active:bg-amber-900/20'
              }`}
            >
              <ExclamationTriangleIcon className="h-5 w-5" />
              {tx.check_status === 'flagged' ? 'Beanstandet' : 'Beanstanden'}
            </button>
          </div>
        </div>

        {approval && (
          <div className="mt-3 flex items-center gap-1.5 border-t border-green-100 pt-2 text-[11px] leading-5 text-green-700 dark:border-green-900/40 dark:text-green-400">
            <CheckCircleIcon className="h-3.5 w-3.5 shrink-0" />
            <span>{approval}</span>
          </div>
        )}
      </article>
    );
  };

  const renderDesktopRow = (tx: Transaction) => {
    const docs = belegeByBuchung[tx.id] || [];
    const isSelected = selectedTransactionIds.has(tx.id);
    const isFlagged = tx.check_status === 'flagged';
    const approval = approvalText(tx, currentMemberId);
    const approvedByOther = tx.check_status === 'confirmed' && tx.own_check_status !== 'confirmed';
    return (
      <React.Fragment key={tx.id}>
      <tr
        className={`group border-b border-gray-100 align-top transition-[background-color,box-shadow] duration-150 dark:border-gray-800 ${
          isFlagged
            ? `${isSelected ? 'bg-amber-50/50 dark:bg-amber-900/20' : ''} hover:bg-amber-50/80 hover:shadow-[inset_3px_0_0_0_#f59e0b] dark:hover:bg-amber-900/30`
            : isSelected
              ? 'bg-primary-50/70 hover:bg-primary-100/70 hover:shadow-[inset_3px_0_0_0_#22c55e] dark:bg-primary-900/10 dark:hover:bg-primary-900/20'
              : 'hover:bg-primary-50/40 hover:shadow-[inset_3px_0_0_0_#22c55e] dark:hover:bg-primary-900/10'
        }`}
      >
        <td className="px-4 py-2.5 text-center">
          {!isCompleted && !approvedByOther && (
            <input
              type="checkbox"
              checked={selectedTransactionIds.has(tx.id)}
              onChange={() => toggleTransactionSelection(tx.id)}
              aria-label={`Buchung vom ${fmtDate(tx.buchungstag)} auswählen`}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          )}
        </td>
        <td className="px-4 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{fmtDate(tx.buchungstag)}</td>
        <td className="px-4 py-2 whitespace-nowrap text-gray-600 dark:text-gray-400">{tx.konto_name || '—'}</td>
        <td className="px-4 py-2 max-w-md">
          <div className={`font-medium text-gray-800 transition-colors dark:text-gray-100 ${
            isFlagged
              ? 'group-hover:text-amber-700 dark:group-hover:text-amber-300'
              : 'group-hover:text-primary-700 dark:group-hover:text-primary-300'
          }`}>{tx.name_zahlungsbeteiligter || tx.buchungstext || '—'}</div>
          <div className="text-gray-500 dark:text-gray-400 truncate">{tx.verwendungszweck || ''}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {txCategories(tx).map((category, index) => (
              <span
                key={`${category.id ?? 'uncategorized'}-${index}`}
                title={`Kategorieanteil: ${fmtMoney(category.betrag)}`}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
              >
                <TagIcon className="h-3 w-3" />
                <span>{category.name}</span>
                <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">·</span>
                <span className="tabular-nums">{fmtMoney(category.betrag)}</span>
              </span>
            ))}
          </div>
          {tx.check_status === 'flagged' && tx.check_note && (
            <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">⚠ {tx.check_note}</div>
          )}
        </td>
        <td className={`px-4 py-2 text-right whitespace-nowrap font-semibold ${Number(tx.betrag) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {fmtMoney(tx.betrag)}
        </td>
        <td className="px-4 py-2 text-center">
          {docs.length > 0 ? (
            <div className="flex flex-col items-center gap-1">
              {docs.map(d => (
                <button key={d.id} onClick={() => openDocument(d)}
                  className="inline-flex items-center gap-1 text-primary-600 hover:underline text-xs">
                  <DocumentTextIcon className="h-4 w-4" /> Beleg
                </button>
              ))}
            </div>
          ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
        </td>
        <td className="px-4 py-2">
          <div className="flex items-center justify-center gap-1">
            <button
              disabled={isCompleted || approvedByOther}
              onClick={() => setCheck(tx, tx.own_check_status === 'confirmed' ? 'none' : 'confirmed')}
              title={approvedByOther ? `Bereits von ${tx.approved_by_name || 'einem Mitprüfer'} genehmigt` : 'Bestätigen'}
              className={`p-1.5 rounded-lg border transition-colors disabled:opacity-40 ${
                tx.check_status === 'confirmed'
                  ? 'bg-green-600 text-white border-green-600'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-green-50 dark:hover:bg-green-900/20'
              }`}
            >
              <CheckCircleIcon className="h-5 w-5" />
            </button>
            <button
              disabled={isCompleted || approvedByOther}
              onClick={() => openNoteModal(tx)}
              title={approvedByOther ? 'Eine bereits genehmigte Buchung kann nicht beanstandet werden' : 'Beanstanden'}
              className={`p-1.5 rounded-lg border transition-colors disabled:opacity-40 ${
                tx.check_status === 'flagged'
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
              }`}
            >
              <ExclamationTriangleIcon className="h-5 w-5" />
            </button>
          </div>
        </td>
      </tr>
      {approval && (
        <tr className="border-b border-green-100 bg-green-50/50 dark:border-green-900/40 dark:bg-green-900/10">
          <td colSpan={7} className="px-4 py-1.5">
            <div className="flex items-center gap-1.5 pl-12 text-[11px] leading-5 text-green-700 dark:text-green-400">
              <CheckCircleIcon className="h-3.5 w-3.5 shrink-0" />
              <span>{approval}</span>
            </div>
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  };

  const groupHeaderMobile = (category: string, items: Transaction[], sum: number) => {
    const isCollapsed = collapsedCategories.has(category);
    const allConfirmed = items.length > 0 && items.every(tx => tx.check_status === 'confirmed');
    return (
      <div key={`h-${category}`} className="flex min-h-11 w-full items-stretch bg-gray-50 dark:bg-gray-800/60">
        <button
          type="button"
          onClick={() => toggleCategory(category)}
          aria-expanded={!isCollapsed}
          title={`${category} ${isCollapsed ? 'aufklappen' : 'einklappen'}`}
          className="flex min-w-0 flex-1 items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <span className="flex min-w-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
            <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
            <TagIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{category}</span>
            <span className="rounded-full bg-gray-200 px-1.5 text-[10px] tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">{items.length}</span>
          </span>
          <span className={`shrink-0 text-xs font-semibold tabular-nums ${sum >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtMoney(sum)}</span>
        </button>
        {!isCompleted && (
          <button
            type="button"
            onClick={() => setCategoryConfirmTarget({
              category,
              transactionIds: items.filter(tx => tx.check_status !== 'confirmed').map(tx => tx.id),
            })}
            disabled={allConfirmed}
            aria-label={`Alle ${items.length} Buchungen in ${category} genehmigen`}
            title={allConfirmed ? 'Alle Buchungen bereits bestätigt' : `Alle Buchungen in ${category} genehmigen`}
            className="inline-flex shrink-0 items-center justify-center gap-1 border-l border-gray-200 px-2.5 text-[11px] font-semibold text-green-700 transition-colors hover:bg-green-50 disabled:cursor-default disabled:text-green-600/50 dark:border-gray-700 dark:text-green-400 dark:hover:bg-green-900/20"
          >
            <CheckCircleIcon className="h-4 w-4" />
            <span>{allConfirmed ? 'Erledigt' : 'Alle'}</span>
          </button>
        )}
      </div>
    );
  };

  const yearActions = (
    <>
      {assignments.map(a => (
        <button
          key={a.id}
          onClick={() => setYear(a.year)}
          className={`px-4 py-2.5 rounded-full text-[13px] font-medium transition-all border ${
            year === a.year
              ? 'bg-primary-500 border-primary-500 text-white'
              : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white hover:border-primary-500'
          }`}
        >
          {a.year}
          {a.status === 'completed' && <span className="ml-2 text-xs">✓</span>}
        </button>
      ))}
    </>
  );

  return (
    <div className="font-sans text-gray-800 dark:text-slate-200">
      <div className="max-w-7xl mx-auto">
        {renderPageHeader(yearActions)}

        <div className="space-y-4 pb-8 sm:space-y-6 sm:pb-10">
          <div className={`${cardClass} flex items-center gap-3 overflow-hidden p-2.5 sm:hidden`}>
            <span className="shrink-0 pl-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Prüfjahr
            </span>
            <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-0.5">
              {yearActions}
            </div>
          </div>

      {isCompleted && (
        <div className={`${cardClass} flex items-center gap-2.5 border-green-300 p-3 dark:border-green-800`}>
          <LockClosedIcon className="h-5 w-5 shrink-0 text-green-600" />
          <span className="text-xs leading-5 text-green-700 sm:text-sm dark:text-green-400">
            Diese Prüfung ist abgeschlossen (nur Lesezugriff).
          </span>
        </div>
      )}

      {year !== null && <KassenpruefungIssues key={year} year={year} onFilter={applyIssueFilter} />}

      {auditors.length > 0 && (
        <section className={`${cardClass} flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:px-4`} aria-label={`Prüfteam ${year}`}>
          <div className="flex shrink-0 items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400">
              <UserGroupIcon className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xs font-semibold text-gray-800 dark:text-gray-100">Prüfteam {year}</h2>
              <p className="text-[10px] text-gray-400 dark:text-gray-500">Gemeinsamer Prüfstatus</p>
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap gap-1.5 sm:border-l sm:border-gray-100 sm:pl-4 dark:sm:border-gray-700">
            {auditors.map(auditor => {
              const fullName = `${auditor.vorname || ''} ${auditor.name || ''}`.trim() || 'Unbekannter Prüfer';
              const initials = `${auditor.vorname?.[0] || ''}${auditor.name?.[0] || ''}`.toUpperCase() || 'KP';
              return (
                <span
                  key={auditor.id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] font-medium text-gray-700 dark:border-gray-700 dark:bg-[#151c28] dark:text-gray-200"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-[8px] font-bold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                    {initials}
                  </span>
                  {fullName}
                </span>
              );
            })}
          </div>
        </section>
      )}

      {/* Kennzahlen */}
      <section className={`${cardClass} overflow-hidden`} aria-labelledby="financial-summary-heading">
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3.5 py-2.5 sm:px-5 dark:border-gray-700">
            <h2 id="financial-summary-heading" className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
              Finanzübersicht
            </h2>
            <span className="text-[10px] text-gray-400 sm:text-xs">Einnahmen − Ausgaben · ohne Umbuchungen</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 sm:divide-x sm:divide-gray-100 dark:sm:divide-gray-700">
            <div className="min-w-0 border-b border-gray-100 px-3 py-3 sm:border-b-0 sm:px-5 sm:py-4 dark:border-gray-700">
              <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                <ArrowTrendingUpIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                <span className="truncate text-[9px] font-semibold uppercase tracking-wider sm:text-[10px]">Einnahmen</span>
              </div>
              <p className="mt-1 whitespace-nowrap text-[clamp(0.95rem,4.7vw,1.5rem)] font-bold leading-tight tabular-nums tracking-tight text-green-600 dark:text-green-400">
                {fmtMoney(totals?.einnahmen ?? 0)}
              </p>
            </div>

            <div className={`order-last col-span-2 min-w-0 px-3 py-3 sm:order-none sm:col-span-1 sm:px-5 sm:py-4 ${balance >= 0 ? 'bg-green-50/70 dark:bg-green-900/10' : 'bg-red-50/70 dark:bg-red-900/10'}`}>
              <div className={`flex items-center gap-1 ${balance >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                <CalculatorIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                <span className="truncate text-[9px] font-semibold uppercase tracking-wider sm:text-[10px]">Bilanz</span>
              </div>
              <p className={`mt-1 whitespace-nowrap text-[clamp(1rem,5vw,1.5rem)] font-bold leading-tight tabular-nums tracking-tight ${balance >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                {fmtMoney(balance)}
              </p>
            </div>

            <div className="min-w-0 border-b border-l border-gray-100 px-3 py-3 sm:border-b-0 sm:border-l-0 sm:px-5 sm:py-4 dark:border-gray-700">
              <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                <ArrowTrendingDownIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                <span className="truncate text-[9px] font-semibold uppercase tracking-wider sm:text-[10px]">Ausgaben</span>
              </div>
              <p className="mt-1 whitespace-nowrap text-[clamp(0.95rem,4.7vw,1.5rem)] font-bold leading-tight tabular-nums tracking-tight text-red-600 dark:text-red-400">
                {fmtMoney(totals?.ausgaben ?? 0)}
              </p>
            </div>
          </div>

          <div className="border-t border-gray-100 px-3.5 py-3 sm:px-5 dark:border-gray-700">
            <div className="mb-1.5 flex items-center justify-between gap-3 text-[10px] text-gray-500 sm:text-xs dark:text-gray-400">
              <span className="font-medium">Prüffortschritt</span>
              <span className="tabular-nums">
                {progress?.checked ?? 0} von {progress?.total ?? 0} · {progressPercent}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-label="Prüffortschritt"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPercent}
              className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
            >
              <div
                className="h-full rounded-full bg-primary-500 transition-[width] duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="mt-1.5 flex items-center gap-3 text-[10px] sm:text-[11px]">
              <span className="text-green-600 dark:text-green-400">{progress?.confirmed ?? 0} bestätigt</span>
              <span className="text-amber-600 dark:text-amber-400">{progress?.flagged ?? 0} beanstandet</span>
            </div>
          </div>
      </section>

      {/* Haushaltsplan des Prüfjahres */}
      <section className={`${cardClass} overflow-hidden`} aria-labelledby="budget-comparison-heading">
        <div className="flex flex-col gap-3 p-3.5 sm:flex-row sm:items-center sm:p-5">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${
              budgetComparison?.hasPlan
                ? 'border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300'
                : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-300'
            }`}>
              <ChartBarSquareIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 id="budget-comparison-heading" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Haushaltsplanvergleich
                </h2>
                {budgetComparison?.hasPlan && (
                  <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:border-green-900/60 dark:bg-green-900/20 dark:text-green-300">
                    Planung für {year} vorhanden
                  </span>
                )}
              </div>
              {!budgetComparison ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Planung für das Prüfjahr wird abgeglichen…</p>
              ) : selectedBudgetPlan ? (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-700 dark:text-gray-200">{selectedBudgetPlan.name}</span>
                  <span>Plan-Ausgaben {fmtMoney(selectedBudgetPlan.totals.planned_expense)}</span>
                  <span>Ist {fmtMoney(selectedBudgetPlan.totals.actual_expense)}</span>
                  {budgetComparison.plans.length > 1 && <span>{budgetComparison.plans.length} Planungen verfügbar</span>}
                </div>
              ) : (
                <p className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300">
                  Für das Prüfjahr {year} ist unter „Budgetplanung“ kein Haushaltsplan hinterlegt.
                </p>
              )}
            </div>
          </div>

          {selectedBudgetPlan && (
            <div className="flex w-full gap-2 sm:w-auto sm:shrink-0">
              <button
                type="button"
                onClick={() => setBudgetModalOpen(true)}
                className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-primary-600 bg-primary-600 px-3.5 text-xs font-semibold text-white transition-colors hover:bg-primary-700 sm:flex-none"
              >
                <ChartBarSquareIcon className="h-4 w-4" />
                Vergleich öffnen
              </button>
              <button
                type="button"
                onClick={downloadBudgetComparisonPdf}
                disabled={budgetPdfLoading}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-[#151c28] dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{budgetPdfLoading ? 'Erstellt…' : 'PDF'}</span>
              </button>
            </div>
          )}
        </div>
        {selectedBudgetPlan && (
          <div className="border-t border-gray-100 px-3.5 py-2.5 sm:px-5 dark:border-gray-700">
            <div className="mb-1.5 flex items-center justify-between gap-3 text-[10px] text-gray-500 dark:text-gray-400">
              <span>Ausgabenbudget ausgeschöpft</span>
              <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                {selectedBudgetPlan.totals.planned_expense > 0
                  ? `${Math.round((selectedBudgetPlan.totals.actual_expense / selectedBudgetPlan.totals.planned_expense) * 100)} %`
                  : 'Kein Ausgabenplan'}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ${
                  selectedBudgetPlan.totals.actual_expense > selectedBudgetPlan.totals.planned_expense
                    ? 'bg-red-500'
                    : selectedBudgetPlan.totals.actual_expense > selectedBudgetPlan.totals.planned_expense * 0.9
                      ? 'bg-amber-500'
                      : 'bg-primary-500'
                }`}
                style={{ width: `${Math.min(100, selectedBudgetPlan.totals.planned_expense > 0 ? (selectedBudgetPlan.totals.actual_expense / selectedBudgetPlan.totals.planned_expense) * 100 : 0)}%` }}
              />
            </div>
          </div>
        )}
      </section>

      {/* Konten (klickbar = Filter) */}
      {accounts.length > 0 && (
        <div className={`${cardClass} overflow-hidden p-3 sm:p-4`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Konten {year}</h2>
            <span className="hidden text-xs text-gray-400 sm:block">Konto filtern oder Kontoauszüge öffnen</span>
            <span className="text-[10px] text-gray-400 sm:hidden">Wischen & auswählen</span>
          </div>
          <div className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-3 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-3">
            {accounts.map(acc => {
              const active = kontoFilter === acc.id;
              const statementCount = Number(acc.kontoauszug_anzahl || 0);
              return (
                <article
                  key={acc.id}
                  className={`min-w-[172px] snap-start overflow-hidden rounded-xl border transition-colors sm:min-w-0 ${
                    active
                      ? 'border-primary-600 ring-1 ring-primary-600 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setKontoFilter(active ? null : acc.id)}
                    aria-pressed={active}
                    className="w-full p-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="truncate text-sm font-semibold text-gray-800 dark:text-gray-100">{acc.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{acc.anzahl} Buchungen</div>
                    <div className={`mt-1 whitespace-nowrap text-sm font-semibold tabular-nums ${Number(acc.summe) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmtMoney(acc.summe)}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => openBankStatements(acc)}
                    disabled={statementCount === 0}
                    className="flex min-h-10 w-full items-center gap-2 border-t border-gray-200 px-3 py-2 text-left text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-default disabled:text-gray-400 disabled:hover:bg-transparent dark:border-gray-700 dark:text-primary-300 dark:hover:bg-primary-900/20 dark:disabled:text-gray-500"
                    aria-label={`${statementCount} ${statementCount === 1 ? 'Kontoauszug' : 'Kontoauszüge'} für ${acc.name} anzeigen`}
                  >
                    <DocumentTextIcon className="h-4 w-4 shrink-0" />
                    <span>{statementCount} {statementCount === 1 ? 'Kontoauszug' : 'Kontoauszüge'}</span>
                    {statementCount > 0 && <EyeIcon className="ml-auto h-4 w-4 shrink-0" />}
                  </button>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {/* Buchungstabelle */}
      <div className={`${cardClass} overflow-hidden`}>
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:flex-wrap sm:items-center sm:p-4 sm:pb-2">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Buchungen {year}</h2>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-gray-500 sm:ml-1 dark:bg-gray-700 dark:text-gray-300">
              {filteredTx.length}{filtersActive ? ` / ${transactions.length}` : ''}
            </span>
          </div>
          <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
            {/* Ansicht: flache Liste vs. nach Kategorie gruppiert */}
            <div className="inline-flex w-full overflow-hidden rounded-lg border border-gray-200 text-xs sm:w-auto dark:border-gray-700">
              {([['date', 'Liste'], ['category', 'Kategorien']] as const).map(([val, label]) => (
                <button key={val} onClick={() => setSortMode(val)}
                  aria-pressed={sortMode === val}
                  className={`inline-flex min-h-9 flex-1 items-center justify-center gap-1 px-2.5 py-1.5 sm:flex-none sm:px-3 ${sortMode === val ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  {val === 'category' && <TagIcon className="h-3.5 w-3.5" />}
                  {label}
                </button>
              ))}
            </div>
            {/* Beleg-Filter */}
            <div className="inline-flex w-full overflow-hidden rounded-lg border border-gray-200 text-xs sm:w-auto dark:border-gray-700">
              {([['all', 'Alle'], ['with', 'Mit Beleg'], ['without', 'Ohne Beleg']] as const).map(([val, label]) => (
                <button key={val} onClick={() => setDocFilter(val)}
                  aria-pressed={docFilter === val}
                  className={`min-h-9 flex-1 px-2.5 py-1.5 sm:flex-none sm:px-3 ${docFilter === val ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  {label}
                </button>
              ))}
            </div>
            {/* Betrags-Filter */}
            <div className="inline-flex w-full overflow-hidden rounded-lg border border-gray-200 text-xs sm:w-auto dark:border-gray-700">
              {([['all', 'Alle'], ['in', 'Einnahmen'], ['out', 'Ausgaben']] as const).map(([val, label]) => (
                <button key={val} onClick={() => setAmountFilter(val)}
                  aria-pressed={amountFilter === val}
                  className={`min-h-9 flex-1 px-2.5 py-1.5 sm:flex-none sm:px-3 ${amountFilter === val ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setHideChecked(value => !value)}
              aria-pressed={hideChecked}
              className={`inline-flex min-h-9 w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors sm:w-auto ${
                hideChecked
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <CheckCircleIcon className="h-4 w-4" /> Geprüfte ausblenden
            </button>
            {filtersActive && (
              <button onClick={resetFilters}
                className="min-h-9 self-start rounded-lg px-1 text-xs font-medium text-primary-600 hover:underline sm:px-3">
                Filter zurücksetzen
              </button>
            )}
            <button
              type="button"
              onClick={downloadCsv}
              disabled={filteredTx.length === 0}
              title="Aktuell angezeigte Buchungen als CSV exportieren"
              className="inline-flex min-h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <ArrowDownTrayIcon className="h-4 w-4" /> CSV exportieren
            </button>
          </div>
        </div>
        {kontoFilter !== null && (
          <div className="mx-3 mb-2 inline-flex items-center rounded-full bg-primary-50 px-2.5 py-1 text-xs text-gray-600 sm:mx-4 dark:bg-primary-900/20 dark:text-gray-300">
            <AttributeLabel iconKey="bankAccount" size="sm">Konto:</AttributeLabel>{' '}
            <span className="ml-1 font-medium text-gray-800 dark:text-gray-100">{accounts.find(a => a.id === kontoFilter)?.name}</span>
          </div>
        )}

        {!isCompleted && filteredTx.length > 0 && (
          <div className="mx-3 mb-3 flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-2.5 sm:mx-4 sm:flex-row sm:items-center dark:border-gray-700 dark:bg-gray-800/50">
            <label className="flex min-h-10 cursor-pointer items-center gap-2 text-sm font-medium text-gray-700 sm:min-h-0 dark:text-gray-200">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleAllFiltered}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Alle angezeigten auswählen
            </label>
            {selectedTransactionIds.size > 0 && (
              <div className="flex flex-col gap-2 sm:ml-auto sm:flex-row sm:flex-wrap sm:items-center">
                <span className="text-xs font-semibold tabular-nums text-primary-700 dark:text-primary-300">
                  {selectedTransactionIds.size} ausgewählt
                </span>
                <button
                  type="button"
                  onClick={() => setBulkConfirmOpen(true)}
                  className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-green-700"
                >
                  <CheckCircleIcon className="h-4 w-4" /> Auswahl bestätigen
                </button>
                <button
                  type="button"
                  onClick={openBulkFlagModal}
                  className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/30"
                >
                  <ExclamationTriangleIcon className="h-4 w-4" /> Auswahl beanstanden
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTransactionIds(new Set())}
                  className="min-h-10 rounded-lg px-3 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Auswahl aufheben
                </button>
              </div>
            )}
          </div>
        )}

        {/* Mobil: priorisierte Buchungskarten statt breiter Tabelle */}
        <div className="divide-y divide-gray-100 border-t border-gray-100 sm:hidden dark:divide-gray-800 dark:border-gray-700">
          {filteredTx.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-400">
              {transactions.length === 0 ? `Keine Buchungen für ${year}` : 'Keine Buchungen für die gewählten Filter'}
            </div>
          ) : sortMode === 'category' ? (
            txGroups.map(g => (
              <React.Fragment key={g.category}>
                {groupHeaderMobile(g.category, g.items, g.sum)}
                {!collapsedCategories.has(g.category) && g.items.map(renderMobileCard)}
              </React.Fragment>
            ))
          ) : (
            <>
              {visibleMobileMonthGroups.map(group => (
                <React.Fragment key={group.key}>
                  {monthDividerMobile(group)}
                  {group.visibleItems.map(renderMobileCard)}
                </React.Fragment>
              ))}
              {mobileVisibleCount < filteredTx.length && (
                <div className="p-3">
                  <button
                    onClick={() => setMobileVisibleCount(count => count + 20)}
                    className="min-h-11 w-full rounded-xl border border-gray-200 text-sm font-medium text-gray-700 dark:border-gray-700 dark:text-gray-200"
                  >
                    Weitere Buchungen anzeigen ({filteredTx.length - mobileVisibleCount})
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <th className="w-12 px-4 py-2 text-center"><span className="sr-only">Auswahl</span></th>
                <th className="px-4 py-2"><AttributeLabel iconKey="date" size="sm">Datum</AttributeLabel></th>
                <th className="px-4 py-2"><AttributeLabel iconKey="bankAccount" size="sm">Konto</AttributeLabel></th>
                <th className="px-4 py-2">Beteiligter / Zweck</th>
                <th className="px-4 py-2 text-right"><AttributeLabel iconKey="amount" size="sm">Betrag</AttributeLabel></th>
                <th className="px-4 py-2 text-center"><AttributeLabel iconKey="receipt" size="sm">Beleg</AttributeLabel></th>
                <th className="px-4 py-2 text-center"><AttributeLabel iconKey="audit" size="sm">Prüfung</AttributeLabel></th>
              </tr>
            </thead>
            {filteredTx.length === 0 ? (
              <tbody>
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  {transactions.length === 0 ? `Keine Buchungen für ${year}` : 'Keine Buchungen für die gewählten Filter'}
                </td></tr>
              </tbody>
            ) : sortMode === 'category' ? (
              txGroups.map(g => (
                <tbody key={g.category}>
                  <tr className="bg-gray-50 dark:bg-gray-800/60">
                    <td colSpan={7} className="p-0">
                      <div className="flex items-stretch">
                        <button
                          type="button"
                          onClick={() => toggleCategory(g.category)}
                          aria-expanded={!collapsedCategories.has(g.category)}
                          title={`${g.category} ${collapsedCategories.has(g.category) ? 'aufklappen' : 'einklappen'}`}
                          className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-2 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          <span className="flex min-w-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                            <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${collapsedCategories.has(g.category) ? '-rotate-90' : ''}`} />
                            <TagIcon className="h-4 w-4 shrink-0 opacity-70" />
                            <span className="truncate">{g.category}</span>
                            <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">{g.items.length}</span>
                          </span>
                          <span className={`shrink-0 text-sm font-semibold tabular-nums ${g.sum >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtMoney(g.sum)}</span>
                        </button>
                        {!isCompleted && (
                          <button
                            type="button"
                            onClick={() => setCategoryConfirmTarget({
                              category: g.category,
                              transactionIds: g.items.filter(tx => tx.check_status !== 'confirmed').map(tx => tx.id),
                            })}
                            disabled={g.items.length > 0 && g.items.every(tx => tx.check_status === 'confirmed')}
                            title={g.items.every(tx => tx.check_status === 'confirmed') ? 'Alle Buchungen bereits bestätigt' : `Alle Buchungen in ${g.category} genehmigen`}
                            className="inline-flex shrink-0 items-center gap-1.5 border-l border-gray-200 px-4 text-xs font-semibold text-green-700 transition-colors hover:bg-green-50 disabled:cursor-default disabled:text-green-600/50 dark:border-gray-700 dark:text-green-400 dark:hover:bg-green-900/20"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            {g.items.every(tx => tx.check_status === 'confirmed') ? 'Alle bestätigt' : 'Alle genehmigen'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {!collapsedCategories.has(g.category) && g.items.map(renderDesktopRow)}
                </tbody>
              ))
            ) : (
              listMonthGroups.map(group => (
                <tbody key={group.key}>
                  {monthDividerDesktop(group)}
                  {group.items.map(renderDesktopRow)}
                </tbody>
              ))
            )}
          </table>
        </div>
      </div>

      {/* Checkliste + Kommentar */}
      <div className={`${cardClass} p-3 sm:p-4`}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Prüf-Checkliste</h2>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-gray-500 dark:bg-gray-700 dark:text-gray-300">
            {checklist.filter(item => item.checked).length}/{checklist.length}
          </span>
        </div>
        <div className="space-y-1.5 sm:space-y-2">
          {checklist.map(item => (
            <label key={item.id} className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border border-gray-100 p-2.5 hover:bg-gray-50 sm:min-h-0 sm:border-0 sm:p-2 dark:border-gray-700 dark:hover:bg-gray-800">
              <input type="checkbox" checked={item.checked} disabled={isCompleted}
                onChange={() => toggleChecklist(item.id)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded text-primary-600 focus:ring-primary-500 sm:h-4 sm:w-4 disabled:opacity-40" />
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-5 text-gray-800 dark:text-gray-100">{item.text}</span>
                {item.description && <span className="mt-0.5 block text-xs leading-4 text-gray-500 dark:text-gray-400">{item.description}</span>}
              </span>
            </label>
          ))}
        </div>

        <h3 className="mb-2 mt-5 text-sm font-semibold text-gray-800 dark:text-gray-100">Gesamtkommentar</h3>
        <textarea
          value={report}
          disabled={isCompleted}
          onChange={e => setReport(e.target.value)}
          rows={4}
          placeholder="Zusammenfassende Bemerkungen zur Prüfung…"
          className="w-full rounded-xl border border-gray-200 bg-white p-3 text-base text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 sm:text-sm dark:border-gray-700 dark:bg-[#111827] dark:text-gray-100 disabled:opacity-60"
        />

        <div className="mt-4 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:gap-3">
          {!isCompleted && (
            <>
              <button onClick={saveChecklist}
                className="min-h-11 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 sm:min-h-0 sm:rounded-lg dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700">
                Speichern
              </button>
              <button onClick={completeAudit}
                className="min-h-11 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 sm:min-h-0 sm:rounded-lg">
                Prüfung abschließen
              </button>
            </>
          )}
          <button onClick={downloadProtocol}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 sm:min-h-0 sm:rounded-lg dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700">
            <ArrowDownTrayIcon className="h-4 w-4" /> Protokoll (PDF)
          </button>
        </div>
      </div>

      {/* Kontoauszüge des ausgewählten Kontos */}
      {statementAccount && (
        <ModalShell
          open={!!statementAccount}
          onClose={() => { if (!statementDownloading) setStatementAccount(null); }}
          variant="form"
          size="lg"
          mobilePresentation="fullscreen"
          icon={DocumentTextIcon}
          accent="brand"
          title={`Kontoauszüge ${statementAccount.name}`}
          subtitle={`Prüfjahr ${year} · ${bankStatements.length || Number(statementAccount.kontoauszug_anzahl || 0)} Dokumente`}
          bodyClassName="bg-gray-50/70 dark:bg-gray-900/20"
          footer={
            <ModalActions
              secondary={{ label: 'Schließen', onClick: () => setStatementAccount(null), disabled: statementDownloading }}
            />
          }
        >
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/20">
            <LockClosedIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
            <div>
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Vertrauliche Finanzunterlagen</p>
              <p className="mt-0.5 text-xs leading-5 text-amber-800 dark:text-amber-300">
                Vorschauen bleiben unverändert. Beim Herunterladen erhält jede PDF-Seite ein persönliches Wasserzeichen; der Zugriff wird protokolliert.
              </p>
            </div>
          </div>

          {statementsLoading ? (
            <div className="flex min-h-44 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              <span className="mr-3 h-5 w-5 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
              Kontoauszüge werden geladen…
            </div>
          ) : statementsError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300">
              {statementsError}
            </div>
          ) : bankStatements.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-gray-700 dark:bg-[#1c2432]">
              <DocumentTextIcon className="mx-auto h-9 w-9 text-gray-300 dark:text-gray-600" />
              <p className="mt-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Keine Kontoauszüge gefunden</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Für dieses Konto überlappt kein Dokument das Prüfjahr.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {bankStatements.map(statement => (
                <article
                  key={statement.id}
                  className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm dark:border-gray-700 dark:bg-[#1c2432]"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
                        <DocumentTextIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-bold text-gray-900 dark:text-gray-100">
                          {fmtDate(statement.zeitraum_von)} – {fmtDate(statement.zeitraum_bis)}
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {[statement.auszugsnummer ? `Auszug ${statement.auszugsnummer}` : null, statement.bank_name, statement.iban_ende ? `IBAN •••• ${statement.iban_ende}` : null, fmtFileSize(statement.document_size)]
                            .filter(Boolean).join(' · ')}
                        </p>
                        <p className="mt-1 truncate text-[11px] text-gray-400 dark:text-gray-500" title={statement.document_name}>
                          {statement.document_name}
                        </p>
                        {(statement.anfangssaldo !== null || statement.endsaldo !== null) && (
                          <p className="mt-1 text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                            Anfang {statement.anfangssaldo === null ? '—' : fmtMoney(statement.anfangssaldo)} · Ende {statement.endsaldo === null ? '—' : fmtMoney(statement.endsaldo)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
                      <button
                        type="button"
                        onClick={() => previewBankStatement(statement)}
                        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-gray-200 px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                      >
                        <EyeIcon className="h-4 w-4" /> Anzeigen
                      </button>
                      <button
                        type="button"
                        onClick={() => setDownloadStatement(statement)}
                        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-primary-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-primary-700"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" /> Download
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </ModalShell>
      )}

      <ConfirmationModal
        visible={!!downloadStatement}
        title="Vertraulichen Kontoauszug herunterladen?"
        message={'Diese PDF enthält **vertrauliche Finanzinformationen**.\n\nAuf jeder Seite werden dein Name, Zeitstempel und eine eindeutige Download-ID diagonal bzw. am Seitenrand markiert. Der Download wird vollständig protokolliert.'}
        type="warning"
        variant="two"
        showCloseButton={!statementDownloading}
        onClose={() => { if (!statementDownloading) setDownloadStatement(null); }}
        buttons={{
          cancel: { text: 'Abbrechen', onPress: () => setDownloadStatement(null) },
          confirm: { text: 'Markiert herunterladen', onPress: confirmBankStatementDownload, loading: statementDownloading },
        }}
      />

      {/* Haushaltsplanvergleich */}
      {budgetModalOpen && selectedBudgetPlan && (
        <ModalShell
          open={budgetModalOpen}
          onClose={() => setBudgetModalOpen(false)}
          variant="form"
          size="xl"
          mobilePresentation="fullscreen"
          icon={ChartBarSquareIcon}
          accent="brand"
          title={`Haushaltsplanvergleich ${year}`}
          subtitle={`${selectedBudgetPlan.name} · Plan und Ist ohne Umbuchungen`}
          bodyClassName="bg-gray-50/70 dark:bg-gray-900/20"
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setBudgetModalOpen(false)}
                className="min-h-10 rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Schließen
              </button>
              <button
                type="button"
                onClick={downloadBudgetComparisonPdf}
                disabled={budgetPdfLoading}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {budgetPdfLoading ? 'PDF wird erstellt…' : 'Als PDF herunterladen'}
              </button>
            </div>
          }
        >
          <div className="space-y-5">
            {budgetComparison && budgetComparison.plans.length > 1 && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-[#151c28]">
                <label htmlFor="budget-plan-select" className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Haushaltsplan auswählen
                </label>
                <select
                  id="budget-plan-select"
                  value={selectedBudgetPlan.id}
                  onChange={event => setSelectedBudgetPlanId(Number(event.target.value))}
                  className="min-h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-700 dark:bg-[#111827] dark:text-gray-100"
                >
                  {budgetComparison.plans.map(plan => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              {([
                ['Einnahmen', selectedBudgetPlan.totals.planned_income, selectedBudgetPlan.totals.actual_income, selectedBudgetPlan.totals.income_variance, 'income'],
                ['Ausgaben', selectedBudgetPlan.totals.planned_expense, selectedBudgetPlan.totals.actual_expense, selectedBudgetPlan.totals.expense_variance, 'expense'],
                ['Bilanz', selectedBudgetPlan.totals.planned_balance, selectedBudgetPlan.totals.actual_balance, selectedBudgetPlan.totals.balance_variance, 'balance'],
              ] as const).map(([label, planned, actual, variance, type]) => {
                const favourable = type === 'expense' ? variance <= 0 : variance >= 0;
                return (
                  <div key={label} className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm dark:border-gray-700 dark:bg-[#151c28]">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <div>
                        <span className="block text-[10px] text-gray-400">Plan</span>
                        <span className="text-sm font-semibold tabular-nums text-gray-700 dark:text-gray-200">{fmtMoney(planned)}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] text-gray-400">Ist</span>
                        <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">{fmtMoney(actual)}</span>
                      </div>
                    </div>
                    <div className={`mt-2 border-t pt-2 text-xs font-semibold tabular-nums ${
                      favourable
                        ? 'border-green-100 text-green-700 dark:border-green-900/40 dark:text-green-400'
                        : 'border-red-100 text-red-700 dark:border-red-900/40 dark:text-red-400'
                    }`}>
                      {variance > 0 ? '+' : ''}{fmtMoney(variance)} Abweichung
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#151c28]">
              <div className="border-b border-gray-200 bg-gray-50 px-3.5 py-3 dark:border-gray-700 dark:bg-[#151c28]">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Kategorien im Plan-Ist-Vergleich</h3>
                <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                  Kategorieanteile aus gesplitteten Buchungen werden einzeln berücksichtigt.
                </p>
              </div>

              <div className="divide-y divide-gray-100 sm:hidden dark:divide-gray-800">
                {selectedBudgetPlan.rows.filter(row => (
                  row.planned_income || row.actual_income || row.planned_expense || row.actual_expense
                )).map(row => {
                  const status = row.status === 'exceeded'
                    ? ['Budget überschritten', 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300']
                    : row.status === 'unplanned'
                      ? ['Nicht geplant', 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300']
                      : row.status === 'warning'
                        ? ['Budget fast erreicht', 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300']
                        : row.status === 'below-plan'
                          ? ['Einnahmeziel offen', 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300']
                          : ['Im Plan', 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'];
                  return (
                    <article key={row.category} className="p-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{row.category}</h4>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold ${status[1]}`}>{status[0]}</span>
                      </div>
                      {(row.planned_income > 0 || row.actual_income > 0) && (
                        <div className="mt-3">
                          <div className="flex justify-between text-[11px]">
                            <span className="font-medium text-green-700 dark:text-green-400">Einnahmen</span>
                            <span className="tabular-nums text-gray-600 dark:text-gray-300">Plan {fmtMoney(row.planned_income)} · Ist {fmtMoney(row.actual_income)}</span>
                          </div>
                        </div>
                      )}
                      {(row.planned_expense > 0 || row.actual_expense > 0) && (
                        <div className="mt-3">
                          <div className="mb-1.5 flex justify-between text-[11px]">
                            <span className="font-medium text-red-700 dark:text-red-400">Ausgaben</span>
                            <span className="tabular-nums text-gray-600 dark:text-gray-300">Plan {fmtMoney(row.planned_expense)} · Ist {fmtMoney(row.actual_expense)}</span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                            <div
                              className={`h-full rounded-full ${row.status === 'exceeded' || row.status === 'unplanned' ? 'bg-red-500' : row.status === 'warning' ? 'bg-amber-500' : 'bg-primary-500'}`}
                              style={{ width: `${Math.min(100, row.planned_expense > 0 ? (row.actual_expense / row.planned_expense) * 100 : (row.actual_expense > 0 ? 100 : 0))}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <div className="hidden overflow-x-auto sm:block">
                <table className="w-full min-w-[760px] text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 dark:bg-[#151c28] dark:text-gray-400">
                    <tr>
                      <th rowSpan={2} className="border-r border-gray-200 px-3 py-2 text-left dark:border-gray-700">Kategorie</th>
                      <th colSpan={3} className="border-r border-gray-200 px-3 py-2 text-center text-green-700 dark:border-gray-700 dark:text-green-400">Einnahmen</th>
                      <th colSpan={3} className="px-3 py-2 text-center text-red-700 dark:text-red-400">Ausgaben</th>
                    </tr>
                    <tr className="border-t border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-2 text-right">Plan</th><th className="px-3 py-2 text-right">Ist</th><th className="border-r border-gray-200 px-3 py-2 text-right dark:border-gray-700">Abw.</th>
                      <th className="px-3 py-2 text-right">Plan</th><th className="px-3 py-2 text-right">Ist</th><th className="px-3 py-2 text-right">Abw.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {selectedBudgetPlan.rows.filter(row => (
                      row.planned_income || row.actual_income || row.planned_expense || row.actual_expense
                    )).map(row => (
                      <tr key={row.category} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                        <td className="border-r border-gray-100 px-3 py-2.5 font-medium text-gray-800 dark:border-gray-800 dark:text-gray-100">{row.category}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmtMoney(row.planned_income)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-gray-800 dark:text-gray-100">{fmtMoney(row.actual_income)}</td>
                        <td className={`border-r border-gray-100 px-3 py-2.5 text-right font-semibold tabular-nums dark:border-gray-800 ${row.income_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {row.income_variance > 0 ? '+' : ''}{fmtMoney(row.income_variance)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmtMoney(row.planned_expense)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-gray-800 dark:text-gray-100">{fmtMoney(row.actual_expense)}</td>
                        <td className={`px-3 py-2.5 text-right font-semibold tabular-nums ${row.expense_variance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {row.expense_variance > 0 ? '+' : ''}{fmtMoney(row.expense_variance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-[11px] leading-5 text-gray-500 dark:text-gray-400">
              Umbuchungen sind ausgeschlossen. „Nicht kategorisiert“ weist Buchungsanteile aus, denen noch keine Kategorie zugeordnet wurde.
            </p>
          </div>
        </ModalShell>
      )}

      {/* Beanstandungs-Modal */}
      {bulkConfirmOpen && (
        <ModalShell
          open={bulkConfirmOpen}
          onClose={() => { if (!bulkConfirming) setBulkConfirmOpen(false); }}
          variant="confirm"
          size="sm"
          icon={CheckCircleIcon}
          accent="brand"
          iconTone="accent"
          title="Buchungen bestätigen"
          subtitle={`${selectedConfirmLabel} noch nicht genehmigt`}
          closeOnBackdrop={!bulkConfirming}
          closeOnEscape={!bulkConfirming}
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => setBulkConfirmOpen(false), disabled: bulkConfirming }}
              primary={{
                label: `${selectedConfirmLabel} bestätigen`,
                onClick: confirmSelectedTransactions,
                loading: bulkConfirming,
                disabled: selectedUnconfirmedCount === 0,
              }}
            />
          }
        >
          <p className="text-sm leading-6 text-gray-700 dark:text-gray-200">
            {selectedUnconfirmedCount === 1
              ? 'Soll die ausgewählte Buchung wirklich als geprüft und bestätigt markiert werden?'
              : `Sollen wirklich ${selectedUnconfirmedCount} Buchungen als geprüft und bestätigt markiert werden?`}
          </p>
          <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
            Vorhandene Beanstandungen bei diesen Buchungen werden dabei überschrieben.
          </p>
        </ModalShell>
      )}

      {/* Alle Buchungen einer Kategorie genehmigen */}
      {categoryConfirmTarget && (
        <ModalShell
          open={!!categoryConfirmTarget}
          onClose={() => { if (!bulkConfirming) setCategoryConfirmTarget(null); }}
          variant="confirm"
          size="sm"
          icon={CheckCircleIcon}
          accent="brand"
          iconTone="accent"
          title="Kategorie genehmigen"
          subtitle={`${categoryConfirmTarget.category} · ${categoryConfirmTarget.transactionIds.length} Buchungen`}
          closeOnBackdrop={!bulkConfirming}
          closeOnEscape={!bulkConfirming}
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => setCategoryConfirmTarget(null), disabled: bulkConfirming }}
              primary={{
                label: `${categoryConfirmTarget.transactionIds.length} Buchungen genehmigen`,
                onClick: confirmCategoryTransactions,
                loading: bulkConfirming,
              }}
            />
          }
        >
          <p className="text-sm leading-6 text-gray-700 dark:text-gray-200">
            Sollen wirklich alle {categoryConfirmTarget.transactionIds.length} Buchungen der Kategorie „{categoryConfirmTarget.category}“ bestätigt werden?
          </p>
          <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
            Nur diese Kategorie wird bearbeitet. Vorhandene Beanstandungen darin werden überschrieben.
          </p>
        </ModalShell>
      )}

      {/* Mehrere Buchungen mit demselben Hinweis beanstanden */}
      {bulkFlagOpen && (
        <ModalShell
          open={bulkFlagOpen}
          onClose={() => { if (!bulkFlagging) setBulkFlagOpen(false); }}
          variant="form"
          size="sm"
          icon={ExclamationTriangleIcon}
          accent="brand"
          title="Buchungen beanstanden"
          subtitle={`${selectedBookingLabel} ausgewählt`}
          closeOnBackdrop={false}
          closeOnEscape={!bulkFlagging}
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => setBulkFlagOpen(false), disabled: bulkFlagging }}
              primary={{
                label: `${selectedBookingLabel} beanstanden`,
                onClick: flagSelectedTransactions,
                loading: bulkFlagging,
                disabled: selectedTransactionIds.size === 0 || !bulkNoteText.trim(),
              }}
            />
          }
        >
          <p className="mb-3 text-sm leading-6 text-gray-700 dark:text-gray-200">
            Dieser Hinweis wird bei jeder der ausgewählten Buchungen gespeichert.
          </p>
          <p className="mb-3 text-xs leading-5 text-gray-500 dark:text-gray-400">
            Vorhandene Bestätigungen oder Beanstandungstexte der Auswahl werden dabei ersetzt.
          </p>
          <textarea
            value={bulkNoteText}
            onChange={event => setBulkNoteText(event.target.value)}
            rows={4}
            maxLength={2000}
            autoFocus
            placeholder="Gemeinsamer Grund der Beanstandung…"
            className="w-full rounded-xl border border-gray-200 bg-white p-3 text-base text-gray-800 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 sm:text-sm dark:border-gray-700 dark:bg-[#111827] dark:text-gray-100"
          />
          <div className="mt-1 text-right text-[10px] tabular-nums text-gray-400">{bulkNoteText.length}/2000</div>
        </ModalShell>
      )}

      {/* Beanstandungs-Modal */}
      {noteModal && (
        <ModalShell
          open={!!noteModal}
          onClose={() => setNoteModal(null)}
          variant="form"
          size="sm"
          icon={ExclamationTriangleIcon}
          accent="brand"
          title="Buchung beanstanden"
          subtitle={`${fmtDate(noteModal.tx.buchungstag)} · ${fmtMoney(noteModal.tx.betrag)}`}
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => setNoteModal(null) }}
              primary={{ label: 'Beanstanden', onClick: saveNote }}
            />
          }
        >
          {noteModal.tx.verwendungszweck && (
            <p className="mb-3 text-xs leading-5 text-gray-500 dark:text-gray-400">{noteModal.tx.verwendungszweck}</p>
          )}
          <textarea
            value={noteText} onChange={e => setNoteText(e.target.value)} rows={4} autoFocus
            placeholder="Grund der Beanstandung…"
            className="w-full rounded-xl border border-gray-200 bg-white p-3 text-base text-gray-800 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 sm:text-sm dark:border-gray-700 dark:bg-[#111827] dark:text-gray-100"
          />
        </ModalShell>
      )}

      {/* Zentrale Dokument-/PDF-Vorschau des Frontends */}
      {previewDocument && (
        <DocumentViewer
          documentId={previewDocument.id}
          fileName={previewDocument.name}
          filePath={previewDocument.file_path || previewDocument.name}
          onClose={() => setPreviewDocument(null)}
          readOnly
          variant="modal"
          pdfInitialScale="page-width"
          sourceUrl={previewDocument.sourceUrl}
          allowDownload={previewDocument.allowDownload}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
        </div>
      </div>
    </div>
  );
};

export default Kassenpruefer;
